import { Dispatch } from "redux"
import { toast } from "react-toastify"
import { setDepartmentBudgets, setDepartmentBudgetSearchFields, setDepartmentBudgetsLoading } from "./reducer"
import { IBudgetLineQueryParam } from "models/budget-line"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"

type DeptBudgetRow = {
  description: string
  code: string
  periods: Record<number, number>
  total: number
}

export const downloadDepartmentBudgetsCSV = (
  tableData: DeptBudgetRow[],
  periodTotals: Record<number, number>,
  grandTotal: number,
  financialYear: number
) => {
  const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)
  const periods = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  const monthHeaders = periods.map(p => calendar.periodToMonth(p, "en-CA"))

  const headers = ["Department", "Code", ...monthHeaders, "Total"]

  const dataRows = tableData.map(row => [
    row.description,
    row.code,
    ...periods.map(p => (row.periods[p] ?? 0).toFixed(2)),
    row.total.toFixed(2),
  ])

  const totalRow = [
    "Total", "",
    ...periods.map(p => (periodTotals[p] ?? 0).toFixed(2)),
    grandTotal.toFixed(2),
  ]

  const csv = [headers, ...dataRows, totalRow]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n")

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = `department-budgets-${financialYear}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// GET /budget/:fiscalYear
export const getDepartmentBudgets = (query: IBudgetLineQueryParam) => async (dispatch: Dispatch) => {
  try {
    dispatch(setDepartmentBudgetsLoading(true))
    let queryString = '';
    if (query) {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;

        if (Array.isArray(value)) {
          value.forEach(v => {
            if (v) params.append(key, v);
          });
        } else {
          params.append(key, String(value));
        }
      });

      queryString = params.toString();
    }

    const url = `${process.env.REACT_APP_API_URL}/department-budget/${queryString ? `?${queryString}` : ''}`

    const res = await fetch(url, {
      credentials: "include",
      method: "GET",
    })

    if (!res.ok) {
      const errBody = await res.json()
      throw new Error(errBody.message)
    }

    const data = await res.json()
    dispatch(setDepartmentBudgets(data.data.data))
    return data.data

  } catch (err: any) {
    toast.error(err.message ?? "Error getting department budgets")
    return null

  } finally {
    dispatch(setDepartmentBudgetsLoading(false))
  }
}

export const getDepartmentBudgetsSearchFields = () => async (dispatch: Dispatch) => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/department-budget/search-fields`

    const res = await fetch(url, {
      credentials: "include",
      method: "GET",
    })

    if (!res.ok) {
      const errBody = await res.json()
      throw new Error(errBody.message)
    }

    const data = await res.json()
    dispatch(setDepartmentBudgetSearchFields(data.data))
    return data.data

  } catch (err: any) {
    toast.error(err.message ?? "Error getting department budgets")
    return null

  }
}